import { Stack, Typography } from "@mui/material";
import PatientAssistantEmptyState from "./PatientAssistantEmptyState";
import PatientAssistantResultCard from "./PatientAssistantResultCard";
import { ANALYSIS_FUNCTIONS, EXTRA_FUNCTIONS } from "./catalog";

const CATALOG_ITEMS = [...ANALYSIS_FUNCTIONS, ...EXTRA_FUNCTIONS];
const TITLE_BY_KEY = CATALOG_ITEMS.reduce((accumulator, item) => {
    accumulator[item.key] = item.title;
    return accumulator;
}, {});
const ORDER_BY_KEY = CATALOG_ITEMS.reduce((accumulator, item, index) => {
    accumulator[item.key] = index;
    return accumulator;
}, {});
const TITLE_ALIASES = {
    'riassunti automatici': 'Riassunto',
    'riassunto automatico': 'Riassunto',
    'riassunto': 'Riassunto',
    'analisi per orientamento': 'Analisi per orientamento',
    'rilevamento emozioni e pattern ricorrenti': 'Rilevamento emozioni e pattern ricorrenti',
    'suggerimenti clinici e ipotesi diagnostiche': 'Suggerimenti clinici e ipotesi diagnostiche',
    'alert automatici per segnali di rischio': 'Alert automatici per segnali di rischio',
    'formulazione caso clinico': 'Formulazione caso clinico',
    'trend linguaggio': 'Trend linguaggio',
    'trend argomenti': 'Trend argomenti',
    'timeline percorso': 'Timeline percorso',
    'supporto refertazione': 'Supporto refertazione'
};

const normalizeFunctionTitle = (item) => {
    const titleFromKey = TITLE_BY_KEY[item.functionKey];
    if (titleFromKey) {
        return titleFromKey;
    }

    const normalizedBackendTitle = String(item.title || '').trim().toLowerCase();
    if (TITLE_ALIASES[normalizedBackendTitle]) {
        return TITLE_ALIASES[normalizedBackendTitle];
    }

    const matchedCatalogItem = CATALOG_ITEMS.find((catalogItem) => catalogItem.title.toLowerCase() === normalizedBackendTitle);
    return matchedCatalogItem?.title || item.title || '';
};

const getFunctionOrder = (item) => {
    if (item.functionKey && ORDER_BY_KEY[item.functionKey] !== undefined) {
        return ORDER_BY_KEY[item.functionKey];
    }

    const normalizedTitle = normalizeFunctionTitle(item).toLowerCase();
    const matchedCatalogItem = CATALOG_ITEMS.find((catalogItem) => catalogItem.title.toLowerCase() === normalizedTitle);
    return matchedCatalogItem ? ORDER_BY_KEY[matchedCatalogItem.key] : Number.MAX_SAFE_INTEGER;
};

const buildAnalysisTitle = (orientation) => (
    orientation
        ? `Analisi seduta - ${orientation}`
        : 'Analisi seduta'
);

const PatientAssistantResults = ({ result, orientation = null }) => {
    if (!result?.job) {
        return <PatientAssistantEmptyState />;
    }

    const analysisResults = (result.analysis || [])
        .map((analysis) => ({
            ...analysis,
            title: analysis.key === 'case_formulation'
                ? buildAnalysisTitle(orientation)
                : normalizeFunctionTitle(analysis)
        }))
        .sort((leftItem, rightItem) => getFunctionOrder(leftItem) - getFunctionOrder(rightItem));

    const extraResults = (result.extra || [])
        .map((extraItem) => ({
            ...extraItem,
            title: normalizeFunctionTitle(extraItem)
        }))
        .sort((leftItem, rightItem) => getFunctionOrder(leftItem) - getFunctionOrder(rightItem));
    const selectedExtraFunctions = Array.isArray(result.job?.extraFunctions) ? result.job.extraFunctions : [];
    const shouldShowExtraResults = selectedExtraFunctions.length > 0 && extraResults.length > 0;

    return (
        <Stack spacing={3}>
            <Stack spacing={1.5}>
                <Stack spacing={1.5}>
                    {analysisResults.map((analysis) => (
                        <PatientAssistantResultCard key={analysis.id} result={analysis} jobId={result.job?.id} />
                    ))}
                </Stack>
                {analysisResults.length === 0 && (
                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                        Nessun risultato principale disponibile per questo job.
                    </Typography>
                )}
            </Stack>

            {shouldShowExtraResults && (
                <Stack spacing={1.5}>
                    <Typography variant="h6" fontWeight="bold">Monitoraggio Percorso</Typography>
                    <Stack spacing={1.5}>
                        {extraResults.map((extraItem) => (
                            <PatientAssistantResultCard key={extraItem.id} result={extraItem} jobId={result.job?.id} compact />
                        ))}
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
};

export default PatientAssistantResults;
